<template>
  <div>
    <TopChartSearch class="px-3 pb-2" v-model="search" :data="sortedData" :text="text" :prop="visualization.name_field" @input="scrollToCurrentSearchItem" @close="$emit('update:showSearch', false)" v-if="showSearch" />
    <div class="hvh-relative">
      <HvhChart :visualization="visualization" :options="chartOptions" :isEditMode="true" @item-clicked="$emit('checked-changed', $event, sortedData)" @wheel="scroll" ref="chart" />
      <HvhVerticalScrollBar class="scroll-bar" :total="data.length" :displayed="displayedBarChartData" v-model="scrollBarValue" :markers="searchMarkers" v-if="showScrollBar" />
      <h4 class="text-xs-center hvh-h7" v-if="bottomText">{{ bottomText }}</h4>
      <Loader v-if="showLoader" />
    </div>
  </div>
</template>
  
<script>
import HvhChart from '../charts/HvhChart.vue';
import Loader from '../common/loader/Loader.vue';
import HvhVerticalScrollBar from '../common/scrollBar/HvhVerticalScrollBar.vue';
import TopChartSearch from './TopChartSearch.vue';
import numeral from 'numeral';
import { merge } from 'lodash';
import { isTextWithinLimit, splitSentanceToWords, splitWordsToLines, trimDotEnd } from '../../libraries/charts/chartCommon';
import { findSearchWordSplitted, propToBool } from '../../libraries/common';
export default {
  components: { HvhChart, HvhVerticalScrollBar, Loader, TopChartSearch },
  props: ['data', 'text', 'bottomText', 'isChecked', 'nameField', 'valueField', 'options', 'showSearch', 'showLoader', 'filterObject',],
  data() {
    return {
      displayedBarChartData: 10,
      scrollBarValue: 0,
      chartOptions: merge({
        grid: {
          bottom: '0%',
          containLabel: true,
          left: '20px',
          right: '30px',
          top: '0%'
        },
        yAxis: {
          axisLabel: {
            formatter: (value, ind) => {
              var isTextInLimit = isTextWithinLimit(this.$el.offsetWidth - 20);
              var words = splitSentanceToWords(value);

              if (!this.search.regex || !value.match(this.search.regex)) {
                const splitted = splitWordsToLines(words.map(word => ({ word })), isTextInLimit, 2);
                if (splitted[1]) {
                  splitted[1] = trimDotEnd(splitted[1], isTextInLimit);
                }
                return splitted.map(f => f.sentance).join('\n');
              } else {
                var isCurrent = this.scrollBarValue + 9 - ind === this.search.index;

                var highlightedPositions = [];
                findSearchWordSplitted(words.join(' '), this.search.regex)
                  .reduce((acc, f) => {
                    if (f.isMatch)
                      highlightedPositions.push({ from: acc, length: f.word.length });
                    return acc + f.word.length;
                  }, 0);

                var wordsWithPositions = [];
                words.reduce((from, word) => {
                  var to = from + word.length;
                  var positions = highlightedPositions.filter(f => f.from < to && f.from + f.length > from);
                  wordsWithPositions.push({
                    word,
                    from,
                    highlightedPositions: positions.map(f => ({ from: Math.max(f.from, from) - from, length: Math.min(to, f.from + f.length) - Math.max(f.from, from) }))
                  });
                  return to + 1;
                }, 0);
                var splitted = splitWordsToLines(wordsWithPositions, isTextInLimit, 2);
                if (splitted[1]) {
                  splitted[1] = trimDotEnd(splitted[1], isTextInLimit);
                }
                return splitted
                  .map((sentance) => sentance.usedWords
                    .map(f => {
                      if (f.highlightedPositions.length) {
                        var positions = [...f.highlightedPositions.flatMap(f => [f.from, f.from + f.length]), f.word.length]
                          .reduce((acc, pos) => pos > acc.last() ? [...acc, pos] : acc, [0]);

                        return positions.head()
                          .map((pos, ind) => f.highlightedPositions.find(g => g.from === pos) || { from: pos, length: positions[ind + 1] - pos, noHighlight: true })
                          .map(g => {
                            var wordPart = f.word.substring(g.from, g.from + g.length);
                            return !g.noHighlight ? `{${isCurrent ? 'highlightCurrent' : 'highlight'}|${wordPart}}` : wordPart;
                          }).join('');
                      } else return f.word
                    }).join(' ')).join('\n');
              }
            },
            rich: {
              highlight: {
                backgroundColor: 'yellow',
                height: 16
              },
              highlightCurrent: {
                backgroundColor: '#ff9632',
                height: 16
              }
            },
            inside: true,
          },
          axisTick: {
            show: false
          },
        },
        xAxis: {
          axisLabel: {
            formatter: num => numeral(num).format('0a'),//
          },
        },
      }, (this.options || {})),
      search: {},
      skipNextFilter: false,
    }
  },
  methods: {
    scroll(event) {
      var oldValue = this.scrollBarValue;
      this.scrollBarValue += event.wheelDeltaY < 0 ? 1 : -1;
      this.scrollBarValue = Math.max(0, Math.min(this.scrollBarValue, this.sortedData.length - this.displayedBarChartData));
      if (Math.abs(oldValue - this.scrollBarValue) > 0 || event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    scrollToCurrentSearchItem() {
      if (!this.showScrollBar) {
        this.scrollBarValue = 0;
      } else if (this.search.index > -1)
        this.scrollBarValue = this.search.index;
      this.rerender();
    },
    rerender() {
      this.$nextTick(() => this.$refs.chart.renderChart());
    },
    propToBool() {
      return propToBool;
    },
  },
  computed: {
    sortedData() {
      return (this.valueField && this.data.sortBy(this.valueField).reverse()) || this.data;
    },
    visualization() {
      var dataLength = Math.min(this.sortedData.length, this.displayedBarChartData);
      return {
        visualization_type: 'horizontalbarchart',
        name_field: this.nameField,
        value_field: this.valueField,
        data: this.sortedData
          .filter((_, ind) => ind >= this.scrollBarValue && ind < this.scrollBarValue + dataLength)
          .map(f => ({
            ...f,
            ...(this.isChecked && this.isChecked(f) ? { color: '#37ae8c' } : {}),
            unchecked: this.isChecked && this.sortedData.find(f => this.isChecked(f)) && !this.isChecked(f)
          })),
      };
    },
    // filterHash() {
    //   return hashCode(this.filterObject || {});
    // },
    searchMarkers() {
      return (this.search.foundItems || []).map(f => ({ value: this.sortedData.indexOf(f) }));
    },
    showScrollBar() {
      return this.sortedData.length > this.displayedBarChartData;
    },
  },
}
</script>

<style lang="scss" scoped>
.scroll-bar {
  position: 'absolute';
  margin: '0';
  top: 0;
  right: 0;
  bottom: 0;
}
</style>